import { useTranslation } from 'react-i18next'

import { CopyButton } from '@/shared/components/copy-button'
import { annotateLines } from '../request'

/**
 * A block of code with the other name for each column beside it.
 *
 * The annotation is a cell in the same row as the line it describes, rather
 * than an absolutely-positioned element offset by `line * 21px` as upstream
 * does it. That number is the `<pre>`'s line height written down a second time,
 * so every annotation on the page slides out of place the moment the font
 * changes -- and it changes with the reader's own text size, which nothing in
 * the application controls.
 */
export function CodeBlock({
  code,
  mapping = {},
  label,
}: {
  code: string
  mapping?: Record<string, string>
  label: string
}) {
  const { t } = useTranslation()
  const annotations = annotateLines(code, mapping)
  const lines = code.split('\n')

  return (
    <div className="bg-muted/50 relative rounded-lg border">
      <div className="absolute top-2 right-2 z-10">
        <CopyButton value={code} label={t('action.copy')} />
      </div>
      {/* One scroll container, and the annotations scroll with the code they
          belong to -- they are part of the same row rather than a second column
          that would drift sideways from it. */}
      <div className="overflow-x-auto p-3 pr-12">
        <table className="w-full border-separate border-spacing-0 font-mono text-xs">
          <caption className="sr-only">{label}</caption>
          <tbody>
            {lines.map((line, index) => (
              // A line is identified by nothing but its position, and the
              // whole block is replaced whenever any of it changes.
              <tr key={index}>
                <td className="pr-6 whitespace-pre">{line}</td>
                <td className="text-muted-foreground w-0 text-right whitespace-nowrap">
                  {annotations[index] ?? ''}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
